import { useState, useEffect, useMemo, useCallback } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import {
  ClipboardList, Users, ShieldCheck, Plus, X, Trash2, Pencil,
  Calendar as CalIcon, Clock, AlertTriangle, Check, Sparkles,
  RefreshCw, FileText, Sparkle, Zap, Printer,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import ChecklistsTab from "../components/ChecklistsTab";

// ───────── Constants ─────────
const TASK_CATEGORIES = [
  { key: "opening",    label: "Opening",     tone: "bg-amber-50 text-amber-700 ring-amber-200" },
  { key: "closing",    label: "Closing",     tone: "bg-violet-50 text-violet-700 ring-violet-200" },
  { key: "cleaning",   label: "Cleaning",    tone: "bg-emerald-50 text-emerald-700 ring-emerald-200" },
  { key: "restock",    label: "Restock",     tone: "bg-sky-50 text-sky-700 ring-sky-200" },
  { key: "front_desk", label: "Front Desk",  tone: "bg-rose-50 text-rose-700 ring-rose-200" },
  { key: "admin",      label: "Admin",       tone: "bg-slate-100 text-slate-700 ring-slate-200" },
  { key: "other",      label: "Other",       tone: "bg-stone-100 text-stone-700 ring-stone-200" },
];
const TASK_PRIORITIES = [
  { key: "low",     label: "Low",     tone: "bg-stone-100 text-stone-600 ring-stone-200" },
  { key: "normal",  label: "Normal",  tone: "bg-sky-50 text-sky-700 ring-sky-200" },
  { key: "high",    label: "High",    tone: "bg-amber-50 text-amber-700 ring-amber-200" },
  { key: "urgent",  label: "Urgent",  tone: "bg-rose-50 text-rose-700 ring-rose-200" },
];
const TASK_STATUSES = [
  { key: "todo",        label: "To do",       tone: "bg-stone-100 text-stone-700" },
  { key: "in_progress", label: "In progress", tone: "bg-sky-50 text-sky-700" },
  { key: "done",        label: "Done",        tone: "bg-emerald-50 text-emerald-700" },
  { key: "skipped",     label: "Skipped",     tone: "bg-stone-100 text-stone-500 line-through" },
];
const MEETING_TYPES = [
  { key: "huddle",      label: "Huddle" },
  { key: "one_on_one",  label: "1:1" },
  { key: "all_hands",   label: "All Hands" },
  { key: "training",    label: "Training" },
  { key: "other",       label: "Other" },
];
const COMPLIANCE_TYPES = [
  { key: "license",       label: "License" },
  { key: "certification", label: "Certification" },
  { key: "training",      label: "Training" },
  { key: "insurance",     label: "Insurance" },
  { key: "permit",        label: "Permit" },
  { key: "other",         label: "Other" },
];
const SANITATION_AREAS = [
  "stations", "shampoo_bowls", "floors", "restroom", "color_bar",
  "towels_laundry", "break_room", "front_desk", "tools_disinfection", "other",
];

const todayISO = () => new Date().toISOString().slice(0, 10);
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
  catch { return iso; }
};
const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
};
const catMeta = (k) => TASK_CATEGORIES.find(c => c.key === k) || TASK_CATEGORIES[6];
const priMeta = (k) => TASK_PRIORITIES.find(c => c.key === k) || TASK_PRIORITIES[1];
const statMeta = (k) => TASK_STATUSES.find(c => c.key === k) || TASK_STATUSES[0];

// ───────── Main Page ─────────
export default function OperationsPage() {
  const [tab, setTab] = useState("checklists");

  return (
    <div className="space-y-6" data-testid="operations-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-serif text-3xl md:text-4xl text-[#072032] tracking-tight">Operations</h1>
          <p className="text-sm text-stone-600 mt-1">
            Opening / closing / downtime lists auto-assigned to whoever is on the clock, plus meetings and compliance.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-stone-200">
        {[
          { key: "checklists", label: "Checklists", Icon: ClipboardList },
          { key: "meetings",   label: "Meetings",   Icon: Users },
          { key: "compliance", label: "Compliance", Icon: ShieldCheck },
        ].map(({ key, label, Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            data-testid={`operations-tab-${key}`}
            className={`px-4 py-2.5 text-sm font-medium flex items-center gap-2 border-b-2 -mb-px transition-colors ${
              tab === key
                ? "border-[#072032] text-[#072032]"
                : "border-transparent text-stone-500 hover:text-[#072032]"
            }`}
          >
            <Icon className="h-4 w-4" />
            {label}
          </button>
        ))}
      </div>

      {tab === "checklists" && <ChecklistsTab />}
      {tab === "meetings"   && <MeetingsTab />}
      {tab === "compliance" && <ComplianceTab />}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
//  TASKS TAB
// ════════════════════════════════════════════════════════════════════
function TasksTab() {
  const [tasks, setTasks] = useState([]);
  const [staff, setStaff] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [summary, setSummary] = useState({ overdue: 0, due_today: 0, unassigned: 0, in_progress: 0, done_today: 0 });
  const [filter, setFilter] = useState({ status: "open", category: "all", assignee_id: "" });
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showTemplates, setShowTemplates] = useState(false);
  const [smartAssignTask, setSmartAssignTask] = useState(null);

  const load = useCallback(async () => {
    try {
      const [tasksRes, staffRes, tmplRes, sumRes] = await Promise.all([
        api.get("/tasks"),
        api.get("/staff/all").catch(() => api.get("/staff")),
        api.get("/task-templates"),
        api.get("/tasks/summary"),
      ]);
      setTasks(tasksRes.data || []);
      setStaff(staffRes.data || []);
      setTemplates(tmplRes.data || []);
      setSummary(sumRes.data || {});
    } catch (e) {
      toast.error("Failed to load tasks");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const visible = useMemo(() => {
    return tasks.filter(t => {
      if (filter.status === "open" && (t.status === "done" || t.status === "skipped")) return false;
      if (filter.status === "done" && t.status !== "done") return false;
      if (filter.status !== "all" && filter.status !== "open" && filter.status !== "done" && t.status !== filter.status) return false;
      if (filter.category !== "all" && t.category !== filter.category) return false;
      if (filter.assignee_id && t.assignee_id !== filter.assignee_id) return false;
      return true;
    });
  }, [tasks, filter]);

  const handleSave = async (payload) => {
    try {
      if (editing) {
        await api.put(`/tasks/${editing.task_id}`, payload);
        toast.success("Task updated");
      } else {
        await api.post("/tasks", payload);
        toast.success("Task created");
      }
      setShowModal(false);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const handleStatus = async (task, newStatus) => {
    try {
      await api.put(`/tasks/${task.task_id}`, { status: newStatus });
      load();
    } catch (e) { toast.error("Update failed"); }
  };

  const handleDelete = async (task) => {
    if (!window.confirm(`Delete "${task.title}"?`)) return;
    try {
      await api.delete(`/tasks/${task.task_id}`);
      toast.success("Task deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const handleGenerate = async () => {
    try {
      const res = await api.post("/task-templates/generate-today");
      toast.success(`Generated ${res.data.created} task(s) for today`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Generate failed"); }
  };

  return (
    <div className="space-y-5">
      {/* Summary pills */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[
          { key: "overdue",     label: "Overdue",     value: summary.overdue,     tone: "bg-rose-50 text-rose-700 ring-rose-200" },
          { key: "due_today",   label: "Due today",   value: summary.due_today,   tone: "bg-amber-50 text-amber-800 ring-amber-200" },
          { key: "unassigned",  label: "Unassigned",  value: summary.unassigned,  tone: "bg-stone-100 text-stone-700 ring-stone-200" },
          { key: "in_progress", label: "In progress", value: summary.in_progress, tone: "bg-sky-50 text-sky-700 ring-sky-200" },
          { key: "done_today",  label: "Done today",  value: summary.done_today,  tone: "bg-emerald-50 text-emerald-700 ring-emerald-200" },
        ].map(s => (
          <div key={s.key} className={`rounded-2xl ring-1 px-4 py-3 ${s.tone}`} data-testid={`tasks-summary-${s.key}`}>
            <div className="text-2xl font-serif">{s.value ?? 0}</div>
            <div className="text-xs uppercase tracking-wider mt-1 opacity-80">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Actions row */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex flex-wrap gap-2">
          <select
            value={filter.status}
            onChange={(e) => setFilter(f => ({ ...f, status: e.target.value }))}
            className="rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm"
            data-testid="tasks-filter-status"
          >
            <option value="open">Open</option>
            <option value="all">All</option>
            <option value="todo">To do</option>
            <option value="in_progress">In progress</option>
            <option value="done">Done</option>
          </select>
          <select
            value={filter.category}
            onChange={(e) => setFilter(f => ({ ...f, category: e.target.value }))}
            className="rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm"
            data-testid="tasks-filter-category"
          >
            <option value="all">All categories</option>
            {TASK_CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
          <select
            value={filter.assignee_id}
            onChange={(e) => setFilter(f => ({ ...f, assignee_id: e.target.value }))}
            className="rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm"
            data-testid="tasks-filter-assignee"
          >
            <option value="">All assignees</option>
            <option value="unassigned" disabled>—</option>
            {staff.map(u => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
          </select>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setShowTemplates(true)}
            data-testid="tasks-templates-btn"
            className="text-sm"
          >
            <FileText className="h-4 w-4 mr-2" />
            Templates ({templates.length})
          </Button>
          <Button
            variant="outline"
            onClick={handleGenerate}
            data-testid="tasks-generate-btn"
            className="text-sm"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Generate today's
          </Button>
          <Button
            onClick={() => { setEditing(null); setShowModal(true); }}
            className="text-sm bg-[#072032] hover:bg-[#0d2e44] text-[#F9F6ED]"
            data-testid="tasks-new-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            New task
          </Button>
        </div>
      </div>

      {/* Task list */}
      <div className="bg-white rounded-2xl ring-1 ring-stone-200 divide-y divide-stone-100">
        {visible.length === 0 ? (
          <div className="p-12 text-center text-stone-500">
            <ClipboardList className="h-10 w-10 mx-auto mb-3 text-stone-300" />
            <p className="font-medium text-stone-700">No tasks here</p>
            <p className="text-sm mt-1">Add one, or generate today's from your templates.</p>
          </div>
        ) : (
          visible.map(t => (
            <TaskRow
              key={t.task_id}
              task={t}
              onToggle={() => handleStatus(t, t.status === "done" ? "todo" : "done")}
              onEdit={() => { setEditing(t); setShowModal(true); }}
              onDelete={() => handleDelete(t)}
              onStatusChange={(s) => handleStatus(t, s)}
              onSmartAssign={() => setSmartAssignTask(t)}
            />
          ))
        )}
      </div>

      {showModal && (
        <TaskModal
          task={editing}
          staff={staff}
          onClose={() => { setShowModal(false); setEditing(null); }}
          onSave={handleSave}
        />
      )}
      {showTemplates && (
        <TemplatesModal
          templates={templates}
          staff={staff}
          onClose={() => setShowTemplates(false)}
          onChange={load}
        />
      )}
      {smartAssignTask && (
        <SmartAssignModal
          task={smartAssignTask}
          onClose={() => setSmartAssignTask(null)}
          onAssigned={() => { setSmartAssignTask(null); load(); }}
        />
      )}
    </div>
  );
}

function TaskRow({ task, onToggle, onEdit, onDelete, onStatusChange, onSmartAssign }) {
  const cat = catMeta(task.category);
  const pri = priMeta(task.priority);
  const stat = statMeta(task.status);
  const isOverdue = task.due_date && task.due_date < todayISO() && task.status !== "done";
  const done = task.status === "done";
  const scheduled = !!task.scheduled_start_time;
  const canSmartAssign = !done && task.status !== "skipped";

  return (
    <div
      className={`flex items-start gap-3 p-4 hover:bg-stone-50 transition-colors ${done ? "opacity-60" : ""}`}
      data-testid={`task-row-${task.task_id}`}
    >
      <button
        onClick={onToggle}
        className={`mt-0.5 h-5 w-5 rounded-md ring-1 flex items-center justify-center shrink-0 transition-colors ${
          done ? "bg-emerald-500 ring-emerald-500 text-white" : "bg-white ring-stone-300 hover:ring-[#072032]"
        }`}
        title={done ? "Mark as to-do" : "Mark complete"}
        data-testid={`task-toggle-${task.task_id}`}
      >
        {done && <Check className="h-3.5 w-3.5" />}
      </button>
      <div className="min-w-0 flex-1">
        <div className="flex items-start gap-2 flex-wrap">
          <span className={`font-medium text-[#072032] ${done ? "line-through" : ""}`}>{task.title}</span>
          <span className={`text-[10px] font-medium uppercase tracking-wider px-2 py-0.5 rounded-full ring-1 ${cat.tone}`}>{cat.label}</span>
          <span className={`text-[10px] font-medium uppercase tracking-wider px-2 py-0.5 rounded-full ring-1 ${pri.tone}`}>{pri.label}</span>
          {task.template_id && (
            <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ring-1 bg-yellow-50 text-amber-800 ring-yellow-200">
              <Sparkle className="inline h-3 w-3 mr-0.5" />
              recurring
            </span>
          )}
          {scheduled && (
            <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ring-1 bg-[#FFE01B]/30 text-amber-900 ring-yellow-300" title={fmtDateTime(task.scheduled_start_time)}>
              <Zap className="inline h-3 w-3 mr-0.5" />
              {new Date(task.scheduled_start_time).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
            </span>
          )}
        </div>
        {task.description && (
          <p className="text-sm text-stone-600 mt-1">{task.description}</p>
        )}
        <div className="text-xs text-stone-500 mt-2 flex flex-wrap gap-x-3 gap-y-1">
          {task.due_date && (
            <span className={isOverdue ? "text-rose-600 font-medium" : ""}>
              <CalIcon className="inline h-3 w-3 mr-1" />
              {isOverdue ? "Overdue · " : "Due "}{fmtDate(task.due_date)}
            </span>
          )}
          <span>
            <Users className="inline h-3 w-3 mr-1" />
            {task.assignee_name || "Unassigned"}
          </span>
          {task.completed_at && (
            <span>
              <Check className="inline h-3 w-3 mr-1 text-emerald-600" />
              by {task.completed_by_name || "?"} · {fmtDateTime(task.completed_at)}
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        {canSmartAssign && (
          <button
            onClick={onSmartAssign}
            className="px-2 py-1 text-xs rounded-md ring-1 bg-[#FFE01B]/20 hover:bg-[#FFE01B]/40 ring-yellow-300 text-amber-900 flex items-center gap-1"
            title="Find a free slot on a stylist's calendar"
            data-testid={`task-smart-assign-${task.task_id}`}
          >
            <Zap className="h-3 w-3" />
            {scheduled ? "Reslot" : "Find slot"}
          </button>
        )}
        <select
          value={task.status}
          onChange={(e) => onStatusChange(e.target.value)}
          className={`text-xs rounded-md ring-1 ring-stone-200 px-1.5 py-1 ${stat.tone}`}
          data-testid={`task-status-${task.task_id}`}
        >
          {TASK_STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        <button onClick={onEdit} className="p-1.5 text-stone-400 hover:text-[#072032]" title="Edit" data-testid={`task-edit-${task.task_id}`}>
          <Pencil className="h-4 w-4" />
        </button>
        <button onClick={onDelete} className="p-1.5 text-stone-400 hover:text-rose-600" title="Delete" data-testid={`task-delete-${task.task_id}`}>
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function SmartAssignModal({ task, onClose, onAssigned }) {
  const [date, setDate] = useState(task.due_date || todayISO());
  const [minMinutes, setMinMinutes] = useState(task.estimated_minutes || 15);
  const [duration, setDuration] = useState(task.estimated_minutes || 15);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/tasks/available-slots", { params: { date, min_minutes: minMinutes } });
      setSlots(res.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load slots");
    } finally {
      setLoading(false);
    }
  }, [date, minMinutes]);
  useEffect(() => { load(); }, [load]);

  const assign = async (userId, gap) => {
    setAssigning(`${userId}-${gap.start_time}`);
    try {
      await api.post(`/tasks/${task.task_id}/schedule`, {
        assignee_id: userId,
        scheduled_start_time: gap.start_time,
        duration_minutes: Math.min(duration, gap.duration_minutes),
      });
      toast.success("Task scheduled");
      onAssigned();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not schedule");
      load(); // refresh — slot may have been taken
    } finally {
      setAssigning(null);
    }
  };

  const eligibleSlots = (s) => s.gaps.filter(g => g.duration_minutes >= duration);

  return (
    <ModalShell title={`Find a slot — "${task.title}"`} onClose={onClose} width="max-w-3xl">
      <div className="space-y-4">
        <div className="grid grid-cols-3 gap-3">
          <FormField label="Date">
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="smart-assign-date" />
          </FormField>
          <FormField label="Task duration (min)">
            <Input
              type="number" min={5} max={480} step={5}
              value={duration}
              onChange={(e) => setDuration(Math.max(5, Number(e.target.value) || 15))}
              data-testid="smart-assign-duration"
            />
          </FormField>
          <FormField label="Min gap shown (min)">
            <Input
              type="number" min={5} max={120} step={5}
              value={minMinutes}
              onChange={(e) => setMinMinutes(Math.max(5, Number(e.target.value) || 15))}
              data-testid="smart-assign-min-gap"
            />
          </FormField>
        </div>

        {loading ? (
          <div className="text-center text-stone-500 py-8 text-sm">Looking for free time…</div>
        ) : slots.length === 0 ? (
          <div className="text-center text-stone-500 py-8">
            <Sparkles className="h-8 w-8 mx-auto text-stone-300 mb-2" />
            <p className="text-sm font-medium text-stone-700">No stylists are scheduled this day</p>
            <p className="text-xs mt-1">Add a shift first via Shift Builder, or pick another date.</p>
          </div>
        ) : (
          <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1" data-testid="smart-assign-results">
            {slots.map(s => {
              const usable = eligibleSlots(s);
              return (
                <div key={s.user_id} className="rounded-xl ring-1 ring-stone-200 p-3" data-testid={`smart-assign-staff-${s.user_id}`}>
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="font-medium text-[#072032] flex items-center gap-2">
                        {s.name}
                        <span className="text-[10px] uppercase tracking-wider text-stone-500">{s.role}</span>
                      </div>
                      <div className="text-xs text-stone-500 mt-0.5">
                        <Clock className="inline h-3 w-3 mr-1" />
                        Shift {new Date(s.shift_start).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
                        {" – "}
                        {new Date(s.shift_end).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
                        <span className="mx-2">·</span>
                        {s.appointment_count} appt{s.appointment_count === 1 ? "" : "s"}
                        <span className="mx-2">·</span>
                        <span className={s.total_free_minutes >= 60 ? "text-emerald-700" : "text-stone-500"}>
                          {Math.round(s.total_free_minutes / 60 * 10) / 10}h free
                        </span>
                      </div>
                    </div>
                  </div>
                  {usable.length === 0 ? (
                    <div className="text-xs text-stone-500 italic">No gap big enough for {duration}min task</div>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {usable.map(gap => {
                        const key = `${s.user_id}-${gap.start_time}`;
                        return (
                          <button
                            key={key}
                            onClick={() => assign(s.user_id, gap)}
                            disabled={assigning === key}
                            className="px-2.5 py-1.5 text-xs rounded-lg ring-1 ring-yellow-300 bg-[#FFE01B]/30 hover:bg-[#FFE01B]/60 hover:ring-[#072032] text-amber-900 disabled:opacity-50 transition-all"
                            data-testid={`smart-assign-slot-${key}`}
                          >
                            <Zap className="inline h-3 w-3 mr-1" />
                            {new Date(gap.start_time).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
                            <span className="text-amber-700/70 ml-1">({gap.duration_minutes}m free)</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      <ModalFooter>
        <Button variant="outline" onClick={onClose}>Close</Button>
      </ModalFooter>
    </ModalShell>
  );
}


function TaskModal({ task, staff, onClose, onSave }) {
  const [form, setForm] = useState({
    title: task?.title || "",
    description: task?.description || "",
    category: task?.category || "other",
    priority: task?.priority || "normal",
    assignee_id: task?.assignee_id || "",
    due_date: task?.due_date || todayISO(),
  });
  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <ModalShell title={task ? "Edit task" : "New task"} onClose={onClose}>
      <div className="space-y-3">
        <FormField label="Title">
          <Input value={form.title} onChange={(e) => update("title", e.target.value)} placeholder="e.g. Wipe down stations after color service" data-testid="task-modal-title" />
        </FormField>
        <FormField label="Description (optional)">
          <textarea
            value={form.description}
            onChange={(e) => update("description", e.target.value)}
            rows={3}
            className="w-full rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm"
            placeholder="Steps, notes, links..."
            data-testid="task-modal-description"
          />
        </FormField>
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Category">
            <select value={form.category} onChange={(e) => update("category", e.target.value)} className="w-full rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm" data-testid="task-modal-category">
              {TASK_CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </FormField>
          <FormField label="Priority">
            <select value={form.priority} onChange={(e) => update("priority", e.target.value)} className="w-full rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm" data-testid="task-modal-priority">
              {TASK_PRIORITIES.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
            </select>
          </FormField>
          <FormField label="Assignee">
            <select value={form.assignee_id} onChange={(e) => update("assignee_id", e.target.value)} className="w-full rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm" data-testid="task-modal-assignee">
              <option value="">Unassigned</option>
              {staff.map(u => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
            </select>
          </FormField>
          <FormField label="Due date">
            <Input type="date" value={form.due_date || ""} onChange={(e) => update("due_date", e.target.value)} data-testid="task-modal-due" />
          </FormField>
        </div>
      </div>
      <ModalFooter>
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button
          onClick={() => {
            if (!form.title.trim()) return toast.error("Title is required");
            onSave({ ...form, assignee_id: form.assignee_id || null, due_date: form.due_date || null });
          }}
          className="bg-[#072032] hover:bg-[#0d2e44] text-[#F9F6ED]"
          data-testid="task-modal-save"
        >
          {task ? "Save changes" : "Create task"}
        </Button>
      </ModalFooter>
    </ModalShell>
  );
}

function TemplatesModal({ templates, staff, onClose, onChange }) {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({
    name: "", title: "", description: "", category: "opening",
    priority: "normal", schedule: "daily", weekday: 0, default_assignee_id: "",
  });

  const create = async () => {
    if (!form.name.trim() || !form.title.trim()) return toast.error("Name & title required");
    try {
      await api.post("/task-templates", {
        ...form,
        default_assignee_id: form.default_assignee_id || null,
        weekday: form.schedule === "weekly" ? Number(form.weekday) : null,
      });
      toast.success("Template created");
      setAdding(false);
      setForm({ name: "", title: "", description: "", category: "opening", priority: "normal", schedule: "daily", weekday: 0, default_assignee_id: "" });
      onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    try { await api.delete(`/task-templates/${id}`); toast.success("Deleted"); onChange(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <ModalShell title="Recurring task templates" onClose={onClose} width="max-w-2xl">
      <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
        {templates.length === 0 && !adding && (
          <p className="text-sm text-stone-500 text-center py-6">No templates yet. Add daily checklists like "Open salon" or "Sanitize stations".</p>
        )}
        {templates.map(t => (
          <div key={t.template_id} className="rounded-xl ring-1 ring-stone-200 p-3 flex items-start gap-3" data-testid={`template-${t.template_id}`}>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-[#072032]">{t.name}</div>
              <div className="text-sm text-stone-600">{t.title}</div>
              <div className="text-xs text-stone-500 mt-1">
                {t.schedule}{t.schedule === "weekly" ? ` · ${["Mon","Tue","Wed","Thu","Fri","Sat","Sun"][t.weekday ?? 0]}` : ""}
                {" · "}{catMeta(t.category).label}
                {t.default_assignee_id && ` · → ${staff.find(s => s.user_id === t.default_assignee_id)?.name || "?"}`}
              </div>
            </div>
            <button onClick={() => remove(t.template_id)} className="p-1.5 text-stone-400 hover:text-rose-600" title="Delete" data-testid={`template-delete-${t.template_id}`}>
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
        {adding ? (
          <div className="rounded-xl ring-1 ring-[#072032]/30 p-3 space-y-2 bg-yellow-50/40">
            <Input value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Template name (e.g. Daily Opening)" data-testid="template-modal-name" />
            <Input value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Task title generated each day" data-testid="template-modal-title" />
            <textarea value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} rows={2} className="w-full rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm" placeholder="Description" />
            <div className="grid grid-cols-2 gap-2">
              <select value={form.category} onChange={(e) => setForm(f => ({ ...f, category: e.target.value }))} className="rounded-lg border border-stone-200 bg-white px-2 py-2 text-sm" data-testid="template-modal-category">
                {TASK_CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
              <select value={form.priority} onChange={(e) => setForm(f => ({ ...f, priority: e.target.value }))} className="rounded-lg border border-stone-200 bg-white px-2 py-2 text-sm">
                {TASK_PRIORITIES.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
              </select>
              <select value={form.schedule} onChange={(e) => setForm(f => ({ ...f, schedule: e.target.value }))} className="rounded-lg border border-stone-200 bg-white px-2 py-2 text-sm" data-testid="template-modal-schedule">
                <option value="daily">Daily</option>
                <option value="weekdays">Weekdays</option>
                <option value="weekends">Weekends</option>
                <option value="weekly">Weekly</option>
              </select>
              {form.schedule === "weekly" && (
                <select value={form.weekday} onChange={(e) => setForm(f => ({ ...f, weekday: e.target.value }))} className="rounded-lg border border-stone-200 bg-white px-2 py-2 text-sm">
                  {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((d, i) => <option key={i} value={i}>{d}</option>)}
                </select>
              )}
              <select value={form.default_assignee_id} onChange={(e) => setForm(f => ({ ...f, default_assignee_id: e.target.value }))} className="rounded-lg border border-stone-200 bg-white px-2 py-2 text-sm col-span-2">
                <option value="">No default assignee</option>
                {staff.map(u => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
              </select>
            </div>
            <div className="flex gap-2 pt-1">
              <Button onClick={create} className="bg-[#072032] hover:bg-[#0d2e44] text-[#F9F6ED] text-sm" data-testid="template-modal-save">Save template</Button>
              <Button variant="outline" onClick={() => setAdding(false)} className="text-sm">Cancel</Button>
            </div>
          </div>
        ) : (
          <Button onClick={() => setAdding(true)} variant="outline" className="w-full" data-testid="template-add-btn">
            <Plus className="h-4 w-4 mr-2" /> Add template
          </Button>
        )}
      </div>
      <ModalFooter>
        <Button variant="outline" onClick={onClose}>Close</Button>
      </ModalFooter>
    </ModalShell>
  );
}

// ════════════════════════════════════════════════════════════════════
//  MEETINGS TAB
// ════════════════════════════════════════════════════════════════════
function MeetingsTab() {
  const [meetings, setMeetings] = useState([]);
  const [staff, setStaff] = useState([]);
  const [filter, setFilter] = useState({ upcoming: true });
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      const [m, s] = await Promise.all([
        api.get("/meetings", { params: { upcoming_only: filter.upcoming } }),
        api.get("/staff/all").catch(() => api.get("/staff")),
      ]);
      setMeetings(m.data || []);
      setStaff(s.data || []);
    } catch { toast.error("Failed to load meetings"); }
  }, [filter.upcoming]);
  useEffect(() => { load(); }, [load]);

  const handleSave = async (payload) => {
    try {
      if (editing) await api.put(`/meetings/${editing.meeting_id}`, payload);
      else await api.post("/meetings", payload);
      toast.success(editing ? "Meeting updated" : "Meeting scheduled");
      setShowModal(false); setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const handleDelete = async (m) => {
    if (!window.confirm(`Delete "${m.title}"?`)) return;
    try { await api.delete(`/meetings/${m.meeting_id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-2">
          <button
            onClick={() => setFilter({ upcoming: true })}
            className={`px-3 py-1.5 rounded-lg text-sm ring-1 ${filter.upcoming ? "bg-[#072032] text-[#F9F6ED] ring-[#072032]" : "bg-white text-stone-600 ring-stone-200"}`}
            data-testid="meetings-filter-upcoming"
          >Upcoming</button>
          <button
            onClick={() => setFilter({ upcoming: false })}
            className={`px-3 py-1.5 rounded-lg text-sm ring-1 ${!filter.upcoming ? "bg-[#072032] text-[#F9F6ED] ring-[#072032]" : "bg-white text-stone-600 ring-stone-200"}`}
            data-testid="meetings-filter-all"
          >All</button>
        </div>
        <Button
          onClick={() => { setEditing(null); setShowModal(true); }}
          className="bg-[#072032] hover:bg-[#0d2e44] text-[#F9F6ED] text-sm"
          data-testid="meetings-new-btn"
        >
          <Plus className="h-4 w-4 mr-2" /> Schedule meeting
        </Button>
      </div>

      <div className="bg-white rounded-2xl ring-1 ring-stone-200 divide-y divide-stone-100">
        {meetings.length === 0 ? (
          <div className="p-12 text-center text-stone-500">
            <Users className="h-10 w-10 mx-auto mb-3 text-stone-300" />
            <p className="font-medium text-stone-700">No meetings scheduled</p>
            <p className="text-sm mt-1">Plan a morning huddle, a 1:1, or an all-hands.</p>
          </div>
        ) : meetings.map(m => (
          <div key={m.meeting_id} className="p-4 hover:bg-stone-50 flex items-start gap-3" data-testid={`meeting-row-${m.meeting_id}`}>
            <div className="rounded-xl bg-yellow-100 ring-1 ring-yellow-200 px-3 py-2 text-center shrink-0">
              <div className="text-[10px] uppercase tracking-wider text-amber-800">
                {new Date(m.start_time).toLocaleDateString("en-US", { month: "short" })}
              </div>
              <div className="text-xl font-serif text-[#072032]">
                {new Date(m.start_time).getDate()}
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start gap-2 flex-wrap">
                <span className="font-medium text-[#072032]">{m.title}</span>
                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ring-1 bg-stone-100 text-stone-700 ring-stone-200">
                  {MEETING_TYPES.find(t => t.key === m.type)?.label || m.type}
                </span>
              </div>
              <div className="text-sm text-stone-600 mt-1">
                <Clock className="inline h-3 w-3 mr-1" />
                {new Date(m.start_time).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
                {" – "}
                {new Date(m.end_time).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
                {m.location && <span> · {m.location}</span>}
              </div>
              {(m.attendees || []).length > 0 && (
                <div className="text-xs text-stone-500 mt-1">
                  <Users className="inline h-3 w-3 mr-1" />
                  {m.attendees.map(a => a.name).join(", ")}
                </div>
              )}
              {m.agenda && <p className="text-sm text-stone-700 mt-2 whitespace-pre-wrap">{m.agenda}</p>}
              {m.notes && (
                <div className="mt-2 text-sm rounded-lg bg-stone-50 p-2 text-stone-700 whitespace-pre-wrap">
                  <span className="text-[10px] uppercase tracking-wider text-stone-500">Notes</span>
                  <div>{m.notes}</div>
                </div>
              )}
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button onClick={() => { setEditing(m); setShowModal(true); }} className="p-1.5 text-stone-400 hover:text-[#072032]" data-testid={`meeting-edit-${m.meeting_id}`}>
                <Pencil className="h-4 w-4" />
              </button>
              <button onClick={() => handleDelete(m)} className="p-1.5 text-stone-400 hover:text-rose-600" data-testid={`meeting-delete-${m.meeting_id}`}>
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {showModal && <MeetingModal meeting={editing} staff={staff} onClose={() => { setShowModal(false); setEditing(null); }} onSave={handleSave} />}
    </div>
  );
}

function MeetingModal({ meeting, staff, onClose, onSave }) {
  const initial = meeting ? {
    title: meeting.title,
    type: meeting.type,
    date: (meeting.start_time || "").slice(0, 10),
    start: (meeting.start_time || "").slice(11, 16),
    end: (meeting.end_time || "").slice(11, 16),
    location: meeting.location || "",
    attendee_ids: (meeting.attendees || []).map(a => a.user_id),
    agenda: meeting.agenda || "",
    notes: meeting.notes || "",
  } : { title: "", type: "huddle", date: todayISO(), start: "09:00", end: "09:30", location: "", attendee_ids: [], agenda: "", notes: "" };
  const [form, setForm] = useState(initial);
  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleAtt = (id) => setForm(f => ({ ...f, attendee_ids: f.attendee_ids.includes(id) ? f.attendee_ids.filter(x => x !== id) : [...f.attendee_ids, id] }));

  return (
    <ModalShell title={meeting ? "Edit meeting" : "Schedule meeting"} onClose={onClose} width="max-w-xl">
      <div className="space-y-3">
        <FormField label="Title"><Input value={form.title} onChange={(e) => update("title", e.target.value)} placeholder="Morning huddle, Q1 1:1, Onboarding..." data-testid="meeting-modal-title" /></FormField>
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Type">
            <select value={form.type} onChange={(e) => update("type", e.target.value)} className="w-full rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm" data-testid="meeting-modal-type">
              {MEETING_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </FormField>
          <FormField label="Date"><Input type="date" value={form.date} onChange={(e) => update("date", e.target.value)} data-testid="meeting-modal-date" /></FormField>
          <FormField label="Start"><Input type="time" value={form.start} onChange={(e) => update("start", e.target.value)} data-testid="meeting-modal-start" /></FormField>
          <FormField label="End"><Input type="time" value={form.end} onChange={(e) => update("end", e.target.value)} data-testid="meeting-modal-end" /></FormField>
        </div>
        <FormField label="Location (optional)"><Input value={form.location} onChange={(e) => update("location", e.target.value)} placeholder="Front desk, color bar, virtual..." /></FormField>
        <FormField label="Attendees">
          <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto p-2 rounded-lg ring-1 ring-stone-200">
            {staff.length === 0 && <span className="text-xs text-stone-500">No staff loaded.</span>}
            {staff.map(u => (
              <button
                key={u.user_id}
                type="button"
                onClick={() => toggleAtt(u.user_id)}
                className={`px-2 py-1 text-xs rounded-full ring-1 ${form.attendee_ids.includes(u.user_id) ? "bg-[#072032] text-[#F9F6ED] ring-[#072032]" : "bg-white text-stone-600 ring-stone-200"}`}
                data-testid={`meeting-attendee-${u.user_id}`}
              >{u.name}</button>
            ))}
          </div>
        </FormField>
        <FormField label="Agenda (optional)">
          <textarea value={form.agenda} onChange={(e) => update("agenda", e.target.value)} rows={3} className="w-full rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm" placeholder="Topics, talking points..." data-testid="meeting-modal-agenda" />
        </FormField>
        <FormField label="Notes (optional)">
          <textarea value={form.notes} onChange={(e) => update("notes", e.target.value)} rows={2} className="w-full rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm" placeholder="Decisions, action items..." data-testid="meeting-modal-notes" />
        </FormField>
      </div>
      <ModalFooter>
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button
          onClick={() => {
            if (!form.title.trim()) return toast.error("Title required");
            if (!form.date || !form.start || !form.end) return toast.error("Date and times required");
            onSave({
              title: form.title,
              type: form.type,
              start_time: `${form.date}T${form.start}:00`,
              end_time: `${form.date}T${form.end}:00`,
              location: form.location,
              attendee_ids: form.attendee_ids,
              agenda: form.agenda,
              notes: form.notes,
            });
          }}
          className="bg-[#072032] hover:bg-[#0d2e44] text-[#F9F6ED]"
          data-testid="meeting-modal-save"
        >
          {meeting ? "Save changes" : "Schedule"}
        </Button>
      </ModalFooter>
    </ModalShell>
  );
}

// ════════════════════════════════════════════════════════════════════
//  COMPLIANCE TAB
// ════════════════════════════════════════════════════════════════════
function ComplianceTab() {
  const [records, setRecords] = useState([]);
  const [staff, setStaff] = useState([]);
  const [sanitation, setSanitation] = useState([]);
  const [alerts, setAlerts] = useState({ count_expired: 0, count_expiring_soon: 0 });
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showSan, setShowSan] = useState(false);

  const load = useCallback(async () => {
    try {
      const [r, s, san, al] = await Promise.all([
        api.get("/compliance/records"),
        api.get("/staff/all").catch(() => api.get("/staff")),
        api.get("/sanitation-logs"),
        api.get("/compliance/alerts"),
      ]);
      setRecords(r.data || []);
      setStaff(s.data || []);
      setSanitation(san.data || []);
      setAlerts(al.data || {});
    } catch { toast.error("Failed to load compliance"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleSave = async (payload) => {
    try {
      if (editing) await api.put(`/compliance/records/${editing.record_id}`, payload);
      else await api.post("/compliance/records", payload);
      toast.success(editing ? "Record updated" : "Record added");
      setShowModal(false); setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const handleDelete = async (r) => {
    if (!window.confirm(`Delete "${r.name}"?`)) return;
    try { await api.delete(`/compliance/records/${r.record_id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  // Open a print-friendly window with the record's metadata + attached
  // document (image inlined, PDF embedded). The window auto-fires
  // `window.print()` once everything paints so staff can pop the
  // license straight onto paper for an inspector.
  const handlePrint = (r) => {
    const fmtDateLocal = (s) => {
      if (!s) return "—";
      try { return new Date(s + "T12:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }); }
      catch { return s; }
    };
    const docUrl = r.document_filename
      ? `${process.env.REACT_APP_BACKEND_URL}/api/compliance/documents/${r.document_filename}`
      : (r.document_url || "");
    const isPdf = (r.document_mime || "").includes("pdf");
    const isImg = (r.document_mime || "").startsWith("image/") || (!r.document_mime && docUrl && /\.(jpe?g|png|webp|heic)$/i.test(docUrl));
    const docHtml = !docUrl
      ? `<p class="muted">No document attached.</p>`
      : isPdf
        ? `<iframe src="${docUrl}" class="doc-frame"></iframe>`
        : isImg
          ? `<img src="${docUrl}" alt="License" class="doc-img" />`
          : `<p class="muted">Document attached: <a href="${docUrl}">${docUrl}</a></p>`;
    const html = `<!doctype html>
<html><head><title>${r.name} — Print</title>
<style>
  body { font-family: Georgia, "Times New Roman", serif; color: #111; max-width: 760px; margin: 32px auto; padding: 0 24px; }
  h1 { font-size: 28px; margin: 0 0 4px; letter-spacing: -0.02em; }
  .meta { color: #444; font-size: 12px; text-transform: uppercase; letter-spacing: 0.15em; }
  hr { border: 0; border-top: 1px solid #ddd; margin: 18px 0; }
  table { width: 100%; border-collapse: collapse; font-size: 14px; }
  td { padding: 6px 0; vertical-align: top; }
  td.lbl { width: 28%; color: #888; font-size: 11px; text-transform: uppercase; letter-spacing: 0.1em; }
  td.val { font-size: 14px; }
  .doc-wrap { margin-top: 24px; }
  .doc-img { max-width: 100%; border: 1px solid #ccc; }
  .doc-frame { width: 100%; height: 800px; border: 1px solid #ccc; }
  .muted { color: #888; }
  .footer { margin-top: 28px; font-size: 11px; color: #888; border-top: 1px solid #eee; padding-top: 12px; }
  @media print { body { margin: 12mm; } .doc-frame { height: 90vh; } }
</style></head>
<body>
  <h1>${r.name}</h1>
  <p class="meta">${(r.record_type || "").replace(/_/g, " ")} · printed ${new Date().toLocaleString("en-US")}</p>
  <hr />
  <table>
    <tr><td class="lbl">Owner</td><td class="val">${r.staff_name || "Business-level"}</td></tr>
    <tr><td class="lbl">Number</td><td class="val">${r.number || "—"}</td></tr>
    <tr><td class="lbl">Issuer</td><td class="val">${r.issuer || "—"}</td></tr>
    <tr><td class="lbl">Issued</td><td class="val">${fmtDateLocal(r.issued_date)}</td></tr>
    <tr><td class="lbl">Expires</td><td class="val">${fmtDateLocal(r.expiry_date)}</td></tr>
    ${r.notes ? `<tr><td class="lbl">Notes</td><td class="val">${r.notes.replace(/</g, "&lt;")}</td></tr>` : ""}
  </table>
  <div class="doc-wrap">${docHtml}</div>
  <p class="footer">SalonFlow OS · Compliance record · ${r.record_id}</p>
  <script>
    // Wait for the image/iframe to lay out before printing so the
    // document fits on the page rather than being cut off.
    function go() { setTimeout(() => window.print(), 400); }
    if (document.images.length) {
      const img = document.images[0];
      if (img.complete) go(); else img.onload = go;
    } else { go(); }
  </script>
</body></html>`;
    const w = window.open("", "_blank", "width=820,height=900");
    if (!w) { toast.error("Pop-up blocked — allow pop-ups to print"); return; }
    w.document.open(); w.document.write(html); w.document.close();
  };
  const logSan = async (area) => {
    try { await api.post("/sanitation-logs", { area }); toast.success(`Logged: ${area.replace(/_/g, " ")}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Log failed"); }
  };

  return (
    <div className="space-y-5">
      {/* Alerts banner */}
      {(alerts.count_expired > 0 || alerts.count_expiring_soon > 0) && (
        <div className="rounded-2xl bg-amber-50 ring-1 ring-amber-200 p-4 flex items-start gap-3" data-testid="compliance-alerts-banner">
          <AlertTriangle className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-medium text-amber-900">
              {alerts.count_expired > 0 && `${alerts.count_expired} expired`}
              {alerts.count_expired > 0 && alerts.count_expiring_soon > 0 && " · "}
              {alerts.count_expiring_soon > 0 && `${alerts.count_expiring_soon} expiring within 30 days`}
            </p>
            <p className="text-sm text-amber-800 mt-0.5">Renew before they lapse to stay audit-ready.</p>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-serif text-xl text-[#072032]">Licenses, certifications & insurance</h2>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowSan(true)} data-testid="compliance-sanitation-btn">
            <Sparkles className="h-4 w-4 mr-2" /> Sanitation log
          </Button>
          <Button onClick={() => { setEditing(null); setShowModal(true); }} className="bg-[#072032] hover:bg-[#0d2e44] text-[#F9F6ED]" data-testid="compliance-new-btn">
            <Plus className="h-4 w-4 mr-2" /> Add record
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-2xl ring-1 ring-stone-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-stone-600 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-2 font-medium">Name</th>
              <th className="text-left px-4 py-2 font-medium">Type</th>
              <th className="text-left px-4 py-2 font-medium">Owner</th>
              <th className="text-left px-4 py-2 font-medium">Expires</th>
              <th className="text-left px-4 py-2 font-medium">Status</th>
              <th className="px-4 py-2 w-12"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {records.length === 0 ? (
              <tr><td colSpan={6} className="p-10 text-center text-stone-500">
                <ShieldCheck className="h-10 w-10 mx-auto mb-3 text-stone-300" />
                <p className="font-medium text-stone-700">No compliance records yet</p>
                <p className="text-xs mt-1">Track licenses, certifications, insurance, and permits in one place.</p>
              </td></tr>
            ) : records.map(r => (
              <tr key={r.record_id} className="hover:bg-stone-50" data-testid={`compliance-row-${r.record_id}`}>
                <td className="px-4 py-3 text-[#072032]">
                  <div className="flex items-center gap-3">
                    {r.document_filename ? (
                      r.document_mime?.startsWith("image/") || !r.document_mime ? (
                        <img src={`${process.env.REACT_APP_BACKEND_URL}/api/compliance/documents/${r.document_filename}`}
                          alt="" className="w-10 h-10 object-cover rounded ring-1 ring-stone-200"
                          data-testid={`compliance-thumb-${r.record_id}`} />
                      ) : (
                        <div className="w-10 h-10 rounded bg-rose-50 ring-1 ring-rose-200 flex items-center justify-center text-rose-700 text-[10px] font-bold">PDF</div>
                      )
                    ) : (
                      <div className="w-10 h-10 rounded bg-stone-100 ring-1 ring-stone-200 flex items-center justify-center text-stone-400">
                        <ShieldCheck className="h-4 w-4" />
                      </div>
                    )}
                    <div>
                      <div className="font-medium">{r.name}</div>
                      {r.number && <div className="text-xs text-stone-500">#{r.number}</div>}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-stone-600">{COMPLIANCE_TYPES.find(t => t.key === r.record_type)?.label || r.record_type}</td>
                <td className="px-4 py-3 text-stone-600">{r.staff_name || "—"}</td>
                <td className="px-4 py-3 text-stone-600">{fmtDate(r.expiry_date)}</td>
                <td className="px-4 py-3"><ExpiryBadge state={r.expiry_state} /></td>
                <td className="px-4 py-3">
                  <div className="flex gap-1">
                    <button onClick={() => handlePrint(r)} className="p-1.5 text-stone-400 hover:text-[#072032]"
                      title="Print" data-testid={`compliance-print-${r.record_id}`}>
                      <Printer className="h-4 w-4" />
                    </button>
                    <button onClick={() => { setEditing(r); setShowModal(true); }} className="p-1.5 text-stone-400 hover:text-[#072032]" data-testid={`compliance-edit-${r.record_id}`}>
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button onClick={() => handleDelete(r)} className="p-1.5 text-stone-400 hover:text-rose-600" data-testid={`compliance-delete-${r.record_id}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Sanitation quick-log card */}
      <div className="bg-white rounded-2xl ring-1 ring-stone-200 p-4">
        <h3 className="font-serif text-lg text-[#072032] mb-3">Quick sanitation log</h3>
        <p className="text-xs text-stone-500 mb-3">Tap an area when it's been sanitized. Logs accumulate to prove daily compliance.</p>
        <div className="flex flex-wrap gap-2">
          {SANITATION_AREAS.map(a => (
            <button
              key={a}
              onClick={() => logSan(a)}
              className="px-3 py-1.5 rounded-full ring-1 ring-stone-200 bg-stone-50 hover:bg-yellow-50 hover:ring-yellow-300 text-xs text-stone-700 capitalize"
              data-testid={`sanitation-log-${a}`}
            >{a.replace(/_/g, " ")}</button>
          ))}
        </div>
        {sanitation.length > 0 && (
          <div className="mt-4 text-xs text-stone-500 max-h-32 overflow-y-auto space-y-1" data-testid="sanitation-recent">
            {sanitation.slice(0, 6).map(s => (
              <div key={s.log_id} className="flex justify-between">
                <span><Check className="inline h-3 w-3 text-emerald-600 mr-1" />{s.area.replace(/_/g, " ")} by {s.completed_by_name}</span>
                <span>{fmtDateTime(s.created_at)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {showModal && <ComplianceModal record={editing} staff={staff} onClose={() => { setShowModal(false); setEditing(null); }} onSave={handleSave} />}
      {showSan && <SanitationModal logs={sanitation} onClose={() => setShowSan(false)} />}
    </div>
  );
}

function ExpiryBadge({ state }) {
  const m = {
    expired:           { label: "Expired",     tone: "bg-rose-50 text-rose-700 ring-rose-200" },
    expiring_soon:     { label: "Expiring",    tone: "bg-amber-50 text-amber-800 ring-amber-200" },
    expiring_quarter:  { label: "<90 days",    tone: "bg-yellow-50 text-yellow-800 ring-yellow-200" },
    ok:                { label: "OK",          tone: "bg-emerald-50 text-emerald-700 ring-emerald-200" },
    no_expiry:         { label: "No expiry",   tone: "bg-stone-100 text-stone-600 ring-stone-200" },
  }[state] || { label: state, tone: "bg-stone-100 text-stone-600 ring-stone-200" };
  return <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ring-1 ${m.tone}`}>{m.label}</span>;
}

function ComplianceModal({ record, staff, onClose, onSave }) {
  const [form, setForm] = useState({
    user_id: record?.user_id || "",
    record_type: record?.record_type || "license",
    name: record?.name || "",
    number: record?.number || "",
    issuer: record?.issuer || "",
    issued_date: record?.issued_date || "",
    expiry_date: record?.expiry_date || "",
    notes: record?.notes || "",
    document_url: record?.document_url || "",
    document_filename: record?.document_filename || "",
    document_mime: record?.document_mime || "",
  });
  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const [uploading, setUploading] = useState(false);
  const docPreviewSrc = form.document_filename
    ? `${process.env.REACT_APP_BACKEND_URL}/api/compliance/documents/${form.document_filename}`
    : (form.document_url || "");

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) { toast.error("File too large (max 15 MB)"); return; }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const res = await api.post("/compliance/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm(f => ({
        ...f,
        document_filename: res.data.document_filename,
        document_mime: res.data.document_mime,
      }));
      toast.success("Photo attached — remember to save");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    }
    setUploading(false);
  };

  return (
    <ModalShell title={record ? "Edit compliance record" : "Add compliance record"} onClose={onClose}>
      <div className="space-y-3">
        <FormField label="Name (e.g. FL Cosmetology License)"><Input value={form.name} onChange={(e) => update("name", e.target.value)} data-testid="compliance-modal-name" /></FormField>
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Type">
            <select value={form.record_type} onChange={(e) => update("record_type", e.target.value)} className="w-full rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm" data-testid="compliance-modal-type">
              {COMPLIANCE_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </FormField>
          <FormField label="Staff (optional)">
            <select value={form.user_id} onChange={(e) => update("user_id", e.target.value)} className="w-full rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm" data-testid="compliance-modal-staff">
              <option value="">— Business-level —</option>
              {staff.map(u => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
            </select>
          </FormField>
          <FormField label="Number"><Input value={form.number} onChange={(e) => update("number", e.target.value)} placeholder="License #" data-testid="compliance-modal-number" /></FormField>
          <FormField label="Issuer"><Input value={form.issuer} onChange={(e) => update("issuer", e.target.value)} placeholder="State board, Aveda…" /></FormField>
          <FormField label="Issued"><Input type="date" value={form.issued_date} onChange={(e) => update("issued_date", e.target.value)} /></FormField>
          <FormField label="Expires"><Input type="date" value={form.expiry_date} onChange={(e) => update("expiry_date", e.target.value)} data-testid="compliance-modal-expiry" /></FormField>
        </div>

        {/* Document upload — phone photo of the actual license/permit. */}
        <FormField label="Document (photo or PDF)">
          <div className="rounded-xl border-2 border-dashed border-stone-200 bg-stone-50 p-3" data-testid="compliance-doc-uploader">
            {form.document_filename ? (
              <div className="flex items-center gap-3">
                {form.document_mime?.startsWith("image/") ? (
                  <img src={docPreviewSrc} alt="License preview"
                    className="w-20 h-20 object-cover rounded-lg ring-1 ring-stone-200"
                    data-testid="compliance-doc-thumb" />
                ) : (
                  <div className="w-20 h-20 rounded-lg bg-rose-50 ring-1 ring-rose-200 flex items-center justify-center text-rose-700 text-xs font-bold">PDF</div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-stone-700 truncate">{form.document_filename.split("__").slice(1).join("__") || form.document_filename}</p>
                  <p className="text-xs text-stone-400">{form.document_mime || "Attached"}</p>
                  <div className="flex gap-2 mt-1.5">
                    <a href={docPreviewSrc} target="_blank" rel="noreferrer"
                      className="text-xs text-[#072032] hover:underline" data-testid="compliance-doc-view">View</a>
                    <button onClick={() => setForm(f => ({ ...f, document_filename: "", document_mime: "" }))}
                      className="text-xs text-rose-600 hover:underline" data-testid="compliance-doc-remove">Remove</button>
                  </div>
                </div>
              </div>
            ) : (
              <label className="flex items-center justify-center gap-2 text-sm text-stone-600 cursor-pointer py-4">
                <Plus className="w-4 h-4" />
                {uploading ? "Uploading…" : "Tap to upload a photo / PDF"}
                <input type="file" accept="image/*,.pdf" capture="environment" onChange={handleFile}
                  data-testid="compliance-doc-input" className="hidden" />
              </label>
            )}
          </div>
        </FormField>

        <FormField label="External link (optional)">
          <Input value={form.document_url} onChange={(e) => update("document_url", e.target.value)}
            placeholder="https:// (Dropbox, Google Drive, etc.)" />
        </FormField>

        <FormField label="Notes">
          <textarea value={form.notes} onChange={(e) => update("notes", e.target.value)} rows={2} className="w-full rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm" placeholder="Renewal steps, document link..." />
        </FormField>
      </div>
      <ModalFooter>
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button
          onClick={() => {
            if (!form.name.trim()) return toast.error("Name required");
            onSave({
              ...form,
              user_id: form.user_id || null,
              issued_date: form.issued_date || null,
              expiry_date: form.expiry_date || null,
            });
          }}
          className="bg-[#072032] hover:bg-[#0d2e44] text-[#F9F6ED]"
          data-testid="compliance-modal-save"
        >{record ? "Save changes" : "Add record"}</Button>
      </ModalFooter>
    </ModalShell>
  );
}

function SanitationModal({ logs, onClose }) {
  return (
    <ModalShell title="Sanitation history (last 7 days)" onClose={onClose} width="max-w-2xl">
      <div className="space-y-1 max-h-[60vh] overflow-y-auto">
        {logs.length === 0 && <p className="text-sm text-stone-500 text-center py-6">No entries yet — log a few areas to start the trail.</p>}
        {logs.map(l => (
          <div key={l.log_id} className="flex justify-between items-center py-2 px-3 rounded-lg hover:bg-stone-50">
            <span className="text-sm text-stone-700 capitalize">
              <Check className="inline h-3 w-3 text-emerald-600 mr-1.5" />
              {l.area.replace(/_/g, " ")} <span className="text-stone-400">·</span> {l.completed_by_name}
            </span>
            <span className="text-xs text-stone-500">{fmtDateTime(l.created_at)}</span>
          </div>
        ))}
      </div>
      <ModalFooter>
        <Button variant="outline" onClick={onClose}>Close</Button>
      </ModalFooter>
    </ModalShell>
  );
}

// ════════════════════════════════════════════════════════════════════
//  SHARED UI HELPERS
// ════════════════════════════════════════════════════════════════════
function ModalShell({ title, onClose, children, width = "max-w-lg" }) {
  return (
    <div className="fixed inset-0 z-50 bg-[#072032]/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="modal-shell">
      <div className={`bg-white rounded-2xl ring-1 ring-stone-200 w-full ${width} shadow-2xl flex flex-col max-h-[90vh]`}>
        <div className="flex items-center justify-between p-4 border-b border-stone-100">
          <h3 className="font-serif text-lg text-[#072032]">{title}</h3>
          <button onClick={onClose} className="p-1.5 text-stone-400 hover:text-[#072032]" data-testid="modal-close">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-4 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}
function ModalFooter({ children }) {
  return <div className="flex justify-end gap-2 pt-4 mt-3 border-t border-stone-100">{children}</div>;
}
function FormField({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wider text-stone-600 font-medium mb-1 block">{label}</span>
      {children}
    </label>
  );
}
